/**
 * SnippetFormModal - Form Modal Component (Multi-Field Support)
 *
 * Displays a focused form modal for filling snippet values.
 * Features:
 * - Multi-field support with radio buttons, text, and textarea
 * - Frontend validation with pattern matching
 * - Real-time error feedback
 * - Mobile-first responsive design
 * - Backward compatible with legacy single-field snippets
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Icon, DynamicIcon } from '@/design-system/atoms/Icon'
import {
  defaultDateValue,
  validateField,
  validateDateRange,
} from './hooks/validateField'
import type {
  AgentSnippet,
  FormField,
  LegacyFormConfig,
  MultiFieldFormConfig,
  SnippetFormValues,
} from '../../types/snippets'

interface SnippetFormModalProps {
  snippet: AgentSnippet | null
  isOpen: boolean
  onClose: () => void
  onSubmit: (
    message: string,
    tool?: string,
    toolParams?: Record<string, unknown>
  ) => void
  metadata?: Record<string, unknown>
}

const isMultiField = (
  config: LegacyFormConfig | MultiFieldFormConfig
): config is MultiFieldFormConfig => {
  return 'fields' in config
}

function validateDateRangeFields(
  fields: FormField[],
  values: Record<string, string | boolean>
): Record<string, string> {
  const errors: Record<string, string> = {}
  fields.forEach(field => {
    if (
      field.field_type === 'date' &&
      field.max_range_days &&
      field.max_range_from_field
    ) {
      const fromValue = (values[field.max_range_from_field] as string) || ''
      const toValue = (values[field.name] as string) || ''
      const error = validateDateRange(
        fromValue,
        toValue,
        field.max_range_days,
        field.max_range_error
      )
      if (error) errors[field.name] = error
    }
  })
  return errors
}

export const SnippetFormModal: React.FC<SnippetFormModalProps> = ({
  snippet,
  isOpen,
  onClose,
  onSubmit,
  metadata,
}) => {
  const [values, setValues] = useState<SnippetFormValues>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const getMetadataValue = useCallback(
    (key: string): string | null => {
      const rawValue = metadata?.[key]
      if (typeof rawValue === 'string') {
        return rawValue.trim()
      }
      if (typeof rawValue === 'number') {
        return String(rawValue)
      }
      return null
    },
    [metadata]
  )

  const matchesChassisSuffixField = (field: FormField): boolean => {
    const haystack = [field.name, field.field_label, field.placeholder || '']
      .join(' ')
      .toLowerCase()

    const hasChassisKeyword = /chassi|chassis/.test(haystack)

    // If field indicates FULL chassis (17 digits, completo), do NOT match as suffix field
    const wantsFullChassis = /17|completo|full/.test(haystack)
    if (wantsFullChassis) {
      return false
    }

    // Only match suffix fields when explicitly asking for suffix/last digits
    const hasSuffixHint = /suffix|ultimos|últimos|7\s*(digitos|dígitos)/.test(
      haystack
    )

    return hasChassisKeyword && hasSuffixHint
  }

  const shouldPrefillLegacyChassisSuffix = (
    snippetConfig: AgentSnippet,
    formConfig: LegacyFormConfig
  ): boolean => {
    const context = [
      snippetConfig.label,
      snippetConfig.description,
      snippetConfig.template,
      formConfig.field_label,
      formConfig.placeholder,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    const hasChassisKeyword = /chassi|chassis/.test(context)

    // If context indicates FULL chassis (17 digits, completo), do NOT prefill with suffix
    const wantsFullChassis = /17|completo|full/.test(context)
    if (wantsFullChassis) {
      return false
    }

    // Only prefill suffix when explicitly asking for suffix/last digits
    const hasSuffixHint = /suffix|ultimos|últimos|7\s*(digitos|dígitos)/.test(
      context
    )

    return hasChassisKeyword && hasSuffixHint
  }

  useEffect(() => {
    if (isOpen && snippet) {
      // Initialize form values
      const initialValues: SnippetFormValues = {}
      const chassisNumber = getMetadataValue('chassis_number')
      const chassisSuffix = getMetadataValue('chassis_suffix')
      const serviceOrder =
        getMetadataValue('service_order_id') ||
        getMetadataValue('service_order')

      if (isMultiField(snippet.form_config)) {
        snippet.form_config.fields.forEach(field => {
          // Handle checkbox fields
          if (field.field_type === 'checkbox') {
            initialValues[field.name] = field.checked_by_default ?? true
            return
          }

          // Handle date fields with default offset
          if (field.field_type === 'date') {
            initialValues[field.name] = defaultDateValue(field)
            return
          }

          let fieldValue = ''
          if (field.field_type === 'radio' && field.default) {
            fieldValue = field.default
          }

          if (
            !fieldValue &&
            chassisSuffix &&
            matchesChassisSuffixField(field)
          ) {
            fieldValue = chassisSuffix
          }

          initialValues[field.name] = fieldValue
        })
      } else {
        // Legacy single-field config
        // Auto-fill chassis from metadata if available for iveco_warranty snippet
        if (snippet.code === 'iveco_warranty' && chassisNumber) {
          initialValues['value'] = chassisNumber
        } else if (snippet.code === 'helpdesk_lookup' && serviceOrder) {
          // Auto-fill service order for helpdesk_lookup snippet
          initialValues['value'] = serviceOrder
        } else if (
          chassisSuffix &&
          shouldPrefillLegacyChassisSuffix(snippet, snippet.form_config)
        ) {
          initialValues['value'] = chassisSuffix
        } else {
          initialValues['value'] = ''
        }
      }

      setValues(initialValues)
      setErrors({})
      setTouched({})
    }
  }, [getMetadataValue, isOpen, snippet])

  if (!isOpen || !snippet) return null

  const { form_config } = snippet

  const handleFieldChange = (
    fieldName: string,
    value: string,
    field?: FormField
  ) => {
    let processedValue = value

    if (field?.text_transform === 'uppercase') {
      processedValue = value.toUpperCase()
    } else if (field?.text_transform === 'lowercase') {
      processedValue = value.toLowerCase()
    }

    setValues(prev => ({ ...prev, [fieldName]: processedValue }))

    if (touched[fieldName] && field) {
      const error = validateField(field, processedValue)
      setErrors(prev => ({
        ...prev,
        [fieldName]: error || '',
      }))
    }
  }

  const handleFieldBlur = (fieldName: string, field?: FormField) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }))

    if (field) {
      const fieldValue = values[fieldName]
      const stringValue = typeof fieldValue === 'string' ? fieldValue : ''
      const error = validateField(field, stringValue)
      setErrors(prev => ({
        ...prev,
        [fieldName]: error || '',
      }))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    const newErrors: Record<string, string> = {}
    const newTouched: Record<string, boolean> = {}
    let hasErrors = false

    if (isMultiField(form_config)) {
      form_config.fields.forEach(field => {
        newTouched[field.name] = true
        // Skip validation for checkbox fields (they're always valid)
        if (field.field_type === 'checkbox') return
        const error = validateField(field, (values[field.name] as string) || '')
        if (error) {
          newErrors[field.name] = error
          hasErrors = true
        }
      })

      const rangeErrors = validateDateRangeFields(form_config.fields, values)
      Object.assign(newErrors, rangeErrors)
      if (Object.keys(rangeErrors).length > 0) hasErrors = true
    } else {
      // Legacy validation
      newTouched['value'] = true
      const legacyValue = values['value']
      const legacyStringValue =
        typeof legacyValue === 'string' ? legacyValue : ''
      if (!legacyStringValue.trim()) {
        newErrors['value'] = 'Campo obrigatório'
        hasErrors = true
      } else if (form_config.pattern) {
        const regex = new RegExp(form_config.pattern)
        if (!regex.test(legacyStringValue)) {
          newErrors['value'] = form_config.error_message || 'Formato inválido'
          hasErrors = true
        }
      }
    }

    setErrors(newErrors)
    setTouched(newTouched)

    if (hasErrors) return

    // Build message from template
    let message = snippet.template

    // Collect checkbox values as toolParams (passed separately, not in message)
    const toolParams: Record<string, unknown> = {}

    if (isMultiField(form_config)) {
      // Replace placeholders in template: {field_name}
      form_config.fields.forEach(field => {
        const value = values[field.name]
        // Handle checkbox fields - add to toolParams, not message
        if (field.field_type === 'checkbox') {
          toolParams[field.name] = value === true
          // Remove placeholder from message if present
          message = message.replace(
            ` | ${field.field_label}: {${field.name}}`,
            ''
          )
          message = message.replace(`{${field.name}}`, '')
          return
        }
        // Handle date fields - add to toolParams AND replace in message
        if (field.field_type === 'date') {
          toolParams[field.name] = value as string
          message = message.replace(`{${field.name}}`, (value as string) || '')
          return
        }
        // Replace literal field names and also handle display labels for radio
        if (field.field_type === 'radio' && field.options) {
          const selectedOption = field.options.find(opt => opt.value === value)
          const displayValue = selectedOption?.label || (value as string)
          message = message.replace(`{${field.name}}`, displayValue)
        } else {
          message = message.replace(`{${field.name}}`, (value as string) || '')
          // Texto também vai como parâmetro: atalho com dois campos de texto (ex.: UT do
          // Tempário) não consegue recuperar cada um a partir da frase da mensagem.
          if (typeof value === 'string' && value.trim()) {
            toolParams[field.name] = value.trim()
          }
        }
      })
    } else {
      // Legacy: smart replacement
      // If template has common placeholders, replace them
      // Otherwise, append value (backwards compatibility)
      const rawValue = values['value']
      const value = typeof rawValue === 'string' ? rawValue : ''
      if (
        /{value}|{chassi}|{chassis}|{query}|{service_order}|{os}/i.test(
          snippet.template
        )
      ) {
        message = snippet.template
          .replace(/{value}/gi, value)
          .replace(/{chassi}/gi, value)
          .replace(/{chassis}/gi, value)
          .replace(/{query}/gi, value)
          .replace(/{service_order}/gi, value)
          .replace(/{os}/gi, value)
      } else {
        message = snippet.template + value
      }
    }

    // Pass toolParams only if there are any
    const hasToolParams = Object.keys(toolParams).length > 0
    onSubmit(message, snippet?.tool, hasToolParams ? toolParams : undefined)
    onClose()
  }

  const renderField = (field: FormField, index: number) => {
    const rawFieldValue = values[field.name]
    const fieldError = errors[field.name]
    const fieldTouched = touched[field.name]

    // Handle checkbox first (boolean value)
    if (field.field_type === 'checkbox') {
      const isChecked = rawFieldValue === true
      return (
        <div key={field.name} className="mb-4">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={e => {
                setValues(prev => ({ ...prev, [field.name]: e.target.checked }))
              }}
              className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
            />
            <span className="text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {field.field_label}
            </span>
          </label>
        </div>
      )
    }

    // For non-checkbox fields, ensure string value
    const fieldValue = typeof rawFieldValue === 'string' ? rawFieldValue : ''

    if (field.field_type === 'radio') {
      return (
        <div key={field.name} className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {field.field_label}
          </label>
          <div className="space-y-2">
            {field.options?.map(option => (
              <label
                key={option.value}
                className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={fieldValue === option.value}
                  onChange={e =>
                    handleFieldChange(field.name, e.target.value, field)
                  }
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                />
                <span className="ml-3 text-gray-900 dark:text-white">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )
    }

    if (field.field_type === 'date') {
      return (
        <div key={field.name} className="mb-6">
          <label
            htmlFor={`field-${field.name}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {field.field_label}
          </label>
          <input
            id={`field-${field.name}`}
            type="text"
            inputMode="numeric"
            value={fieldValue}
            onChange={e => {
              let v = e.target.value.replace(/\D/g, '')
              if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2)
              if (v.length > 5) v = v.slice(0, 5) + '/' + v.slice(5)
              if (v.length > 10) v = v.slice(0, 10)
              handleFieldChange(field.name, v, field)
            }}
            onBlur={() => handleFieldBlur(field.name, field)}
            placeholder={field.placeholder || 'DD/MM/AAAA'}
            maxLength={10}
            autoComplete="off"
            className={`w-full px-4 py-3 text-base rounded-xl border transition-colors ${
              fieldError && fieldTouched
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
            } text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400`}
          />
          {fieldError && fieldTouched && (
            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
              {fieldError}
            </p>
          )}
        </div>
      )
    }

    if (field.field_type === 'textarea') {
      const textValue = fieldValue as string
      return (
        <div key={field.name} className="mb-6">
          <label
            htmlFor={`field-${field.name}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {field.field_label}
          </label>
          <textarea
            id={`field-${field.name}`}
            value={textValue}
            onChange={e => handleFieldChange(field.name, e.target.value, field)}
            onBlur={() => handleFieldBlur(field.name, field)}
            placeholder={field.placeholder}
            rows={field.rows || 4}
            maxLength={field.maxlength}
            autoComplete={field.autocomplete || 'off'}
            autoFocus={index === 0}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-1 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              fieldError && fieldTouched
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:ring-gray-400 dark:focus:ring-gray-500'
            }`}
          />
          {field.maxlength && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
              {textValue.length} / {field.maxlength}
            </div>
          )}
          {fieldError && fieldTouched && (
            <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <Icon name="alert-circle" size={16} />
              {fieldError}
            </div>
          )}
        </div>
      )
    }

    // text input (default fallback)
    return (
      <div key={field.name} className="mb-6">
        <label
          htmlFor={`field-${field.name}`}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {field.field_label}
        </label>
        <input
          id={`field-${field.name}`}
          type="text"
          value={fieldValue as string}
          onChange={e => handleFieldChange(field.name, e.target.value, field)}
          onBlur={() => handleFieldBlur(field.name, field)}
          placeholder={field.placeholder}
          maxLength={field.maxlength}
          autoComplete={field.autocomplete || 'off'}
          autoFocus={index === 0}
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-1 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
            fieldError && fieldTouched
              ? 'border-red-500 focus:ring-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-gray-400 dark:focus:ring-gray-500'
          }`}
        />
        {field.maxlength && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
            {(fieldValue as string).length} / {field.maxlength}
          </div>
        )}
        {fieldError && fieldTouched && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <Icon name="alert-circle" size={16} />
            {fieldError}
          </div>
        )}
      </div>
    )
  }

  const renderLegacyField = () => {
    if (isMultiField(form_config)) return null

    const isTextarea = form_config.field_type === 'textarea'
    const rawValue = values['value']
    const value = typeof rawValue === 'string' ? rawValue : ''
    const error = errors['value']
    const isTouched = touched['value']

    return (
      <div className="mb-6">
        <label
          htmlFor="snippet-input"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {form_config.field_label}
        </label>

        {isTextarea ? (
          <textarea
            id="snippet-input"
            value={value}
            onChange={e => handleFieldChange('value', e.target.value)}
            onBlur={() => handleFieldBlur('value')}
            placeholder={form_config.placeholder}
            rows={form_config.rows || 4}
            maxLength={form_config.maxlength}
            autoComplete={form_config.autocomplete || 'off'}
            autoFocus
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-1 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              error && isTouched
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:ring-gray-400 dark:focus:ring-gray-500'
            }`}
            data-testid="snippet-input"
          />
        ) : (
          <input
            id="snippet-input"
            type="text"
            value={value}
            onChange={e => handleFieldChange('value', e.target.value)}
            onBlur={() => handleFieldBlur('value')}
            placeholder={form_config.placeholder}
            maxLength={form_config.maxlength}
            autoComplete={form_config.autocomplete || 'off'}
            autoFocus
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-1 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              error && isTouched
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:ring-gray-400 dark:focus:ring-gray-500'
            }`}
            data-testid="snippet-input"
          />
        )}

        {form_config.maxlength && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
            {value.length} / {form_config.maxlength}
          </div>
        )}

        {error && isTouched && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <Icon name="alert-circle" size={16} />
            {error}
          </div>
        )}
      </div>
    )
  }

  const hasAnyError = Object.values(errors).some(err => err)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
        data-testid="snippet-form-backdrop"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-t-2xl lg:rounded-2xl shadow-2xl w-full max-w-md animate-scale-in max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DynamicIcon
                  name={snippet.iconName}
                  fallbackValue={snippet.icon}
                  size={32}
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {snippet.label}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {snippet.description}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                aria-label="Fechar"
                data-testid="close-snippet-form"
              >
                <Icon
                  name="x"
                  size={20}
                  className="text-gray-500 dark:text-gray-400"
                />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
            {isMultiField(form_config)
              ? form_config.fields.map((field, index) =>
                  renderField(field, index)
                )
              : renderLegacyField()}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={hasAnyError}
                className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed font-medium transition-colors"
                data-testid="submit-snippet"
              >
                Enviar
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 1024px) {
          @keyframes scale-in {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
        }

        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </>
  )
}
